import type { CSSProperties, ReactNode } from 'react'
import { Link, type LinkProps } from 'react-router-dom'
import { WifiOff } from 'lucide-react'
import { useOnline } from '@/hooks/useOnline'
import { DisabledReason } from '@/components/ui/FormField'

/**
 * Record drawers while offline (UX-018 final review): Send quote, Mark
 * complete, Send reminder, Take payment and the other actions that write must
 * be disabled, and the drawer must say why. The phone sheet covers the global
 * offline banner, so the drawer carries its own line.
 *
 * The shared offline primitive for actions: built on useOnline() and
 * <DisabledReason>. Failed loads offline use <ErrorState> (ui/QueryState),
 * which words them "You're offline" and tells the shell banner.
 */
export const OFFLINE_ACTION_REASON = "You're offline. Sending, payments and stage changes wait until you're back online."

/** One line at the top of a drawer body while offline. */
export function DrawerOfflineNotice({ message = OFFLINE_ACTION_REASON }: { message?: string } = {}) {
  const online = useOnline()
  if (online) return null
  return (
    <div
      role="status"
      data-testid="drawer-offline"
      className="flex items-start gap-2 rounded-xl px-3 py-2 text-[13px]"
      style={{ background: 'var(--midnight-surface)', border: '0.5px solid var(--midnight-border-bright)', color: 'var(--midnight-text2)' }}
    >
      <WifiOff className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      <span>{message}</span>
    </div>
  )
}

/**
 * Wraps action buttons: offline, every button inside is disabled natively
 * (fieldset) and the reason shows under them. Links (Call, Directions) are not
 * buttons and stay usable.
 */
export function OfflineActions({ children, showReason = true }: { children: ReactNode; showReason?: boolean }) {
  const online = useOnline()
  return (
    <>
      <fieldset disabled={!online} className="contents" data-offline={!online || undefined}>
        {children}
      </fieldset>
      {!online && showReason && <DisabledReason className="justify-center">{OFFLINE_ACTION_REASON}</DisabledReason>}
    </>
  )
}

// ─── Single actions that need the network (UX-018) ──────────────────────────
//
// Create, edit and delete actions outside a drawer: the page header's "New
// quote", "Edit", "Delete", the calendar's "New event", the Work board's
// "New", the global "+". Offline they are disabled (not hidden, so the page
// keeps its shape) and say why as their title; the shell banner says it for
// the whole page. Every one carries `data-needs-network`, which the offline
// contract test (tests/offlineActions.contract.test.tsx) checks.

/** Short reason for one disabled action (its title). */
export const OFFLINE_SHORT_REASON = "You're offline. This works again when you're back online."

export interface NetworkActionProps {
  disabled: boolean
  title?: string
  'data-needs-network': ''
}

/**
 * Props for any button that writes or starts a flow that writes:
 *
 *   const net = useNetworkAction()
 *   <button {...net()} onClick={onEdit}>Edit</button>
 *   <button {...net(saving)} …>          // also disabled while saving
 */
export function useNetworkAction(): (alsoDisabled?: boolean) => NetworkActionProps {
  const online = useOnline()
  return (alsoDisabled = false) => ({
    disabled: !online || alsoDisabled,
    title: online ? undefined : OFFLINE_SHORT_REASON,
    'data-needs-network': '',
  })
}

const OFFLINE_LINK_STYLE: CSSProperties = { opacity: 0.5, cursor: 'not-allowed', pointerEvents: 'none' }

/**
 * A link that starts a create flow ("New quote" opens the builder). Offline
 * it renders as a disabled, non-navigating element in the same style.
 */
export function NetworkLink({ className, style, children, ...rest }: LinkProps) {
  const online = useOnline()
  if (online) return <Link className={className} style={style} data-needs-network="" {...rest}>{children}</Link>
  return (
    <span
      role="link"
      aria-disabled="true"
      title={OFFLINE_SHORT_REASON}
      data-needs-network=""
      className={typeof className === 'string' ? className : undefined}
      style={{ ...(style as CSSProperties | undefined), ...OFFLINE_LINK_STYLE }}
    >
      {children}
    </span>
  )
}
